import { useCallback, useState } from 'react';

import { Modal, useMount } from '@simplywallst/ui-core';
import { usePersistentStorage } from '@/hooks/useCoupon';
import { sentryCaptureMessage } from '@/components/Sentry';

import { useInitializePortfolioConnection } from '../hooks/useInitializePortfolioConnection';
import { useGenerateReconnectPortfolioLinkToken } from '../hooks/useGenerateReconnectPortfolioLinkToken';

import { createPortfolioLinkingSDKError } from './utils/createPortfolioLinkingSDKError';
import { getConnectionData } from './utils/getConnectionData';
import { getPlatform } from './utils/getPlatform';
import { PLAID_LINK_TOKEN_KEY } from './constants';

import { PlaidPortfolioLinkingPortal } from './PlaidPortfolioLinkingPortal';

import type {
  PlaidLinkError,
  PlaidLinkOnExitMetadata,
  PlaidLinkOnSuccessMetadata,
} from 'react-plaid-link';
import type { PlaidEvent } from './types';
import type { PortfolioLinkingSDKError } from '../../PortfolioLinking/error/PortfolioLinkingSDKError';

type Props = {
  onError: (error?: PortfolioLinkingSDKError) => void;
  onClose: () => void;
  onSuccess: () => void;
  portfolioId: string;
};

export const PlaidPortfolioReconnector = ({
  onError: handleError,
  onSuccess: handleSuccess,
  onClose,
  portfolioId,
}: Props) => {
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const { set, flush } = usePersistentStorage();

  const { mutate: generateReconnectPortfolioLinkToken } =
    useGenerateReconnectPortfolioLinkToken({
      onError: () => {
        handleError();
      },
      onSuccess: (data) => {
        if (!data) {
          handleError();
          return;
        }
        setLinkToken(data.generateReconnectPortfolioLinkToken.linkToken);
        set(
          PLAID_LINK_TOKEN_KEY,
          data.generateReconnectPortfolioLinkToken.linkToken
        );
      },
    });

  const { mutate: initializePortfolioConnection } =
    useInitializePortfolioConnection({
      onError: () => {
        handleError();
      },
      onSuccess: () => {
        handleSuccess();
      },
    });

  useMount(() => {
    async function getToken() {
      const platform = await getPlatform();
      generateReconnectPortfolioLinkToken({
        provider: 'plaid',
        portfolioId,
        platform,
      });
    }

    getToken();
  });

  const onSuccess = useCallback(
    async (publicToken: string, metadata: PlaidLinkOnSuccessMetadata) => {
      const platform = await getPlatform();
      initializePortfolioConnection({
        connection: getConnectionData(publicToken, metadata),
        provider: 'plaid',
        reconnect: true,
        platform,
      });
    },
    [handleError, handleSuccess, initializePortfolioConnection]
  );

  const onError = useCallback(
    (
      error: PlaidLinkError,
      metadata: PlaidLinkOnExitMetadata,
      events: PlaidEvent[]
    ) => {
      sentryCaptureMessage({
        team: 'wombats',
        message: 'Portfolio Linking Error',
        extra: {
          provider: 'plaid',
          flow: 'reconnection',
          error,
          metadata,
          events,
        },
      });
      flush(PLAID_LINK_TOKEN_KEY);
      handleError(createPortfolioLinkingSDKError(error));
    },
    [flush, handleError]
  );

  if (!linkToken) return <Modal isNaked isLoading />;

  return (
    <PlaidPortfolioLinkingPortal
      linkToken={linkToken}
      onSuccess={onSuccess}
      onError={onError}
      onClose={onClose}
    />
  );
};
